import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Clock, Calendar } from "lucide-react";
import { BOOKING_URL } from "@/config/constants";

const COURSES = [
  {
    title: "Director AI Oversight Intensive",
    duration: "30 days",
    format: "12 modules • practical exercises • templates • certificate",
    description:
      "A structured program for directors who need clarity, confidence, and board-ready governance tools—without the technical noise.",
    outcomes: [
      "A clear board AI oversight model (roles, committees, cadence)",
      "A board-level AI risk view (taxonomy, thresholds, escalation)",
      "Board-ready templates and the Oversight Toolkit",
      "Certificate of completion",
    ],
  },
  {
    title: "Governance Subscription",
    duration: "12 months",
    format: "Ongoing updates • quarterly briefings • advisory access",
    description:
      "Ongoing updates, template refreshes, quarterly briefings, and advisory access—so oversight stays current as AI and regulation evolve.",
    outcomes: [
      "Quarterly regulatory and landscape updates",
      "Refreshed templates and governance artifacts",
      "Priority advisory access for emerging issues",
      "Continuous board readiness",
    ],
  },
];

const CoursesInfoPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="Courses | BoardSight"
        description="Structured AI governance programs for directors. Build defensible oversight frameworks with practical courses and ongoing support."
        canonicalUrl="https://boardsight.ai/courses"
      />

      {/* Hero */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Courses
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Structured programs that equip directors with practical oversight frameworks, board-ready templates, and the questions boards should be asking.
            </p>
          </div>
        </div>
      </section>

      {/* Course Cards */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {COURSES.map((course, index) => (
              <Card key={index} className="bg-card border-border hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {course.title}
                  </CardTitle>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {course.duration}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{course.format}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{course.description}</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-2">What you'll get:</p>
                    <ul className="space-y-2">
                      {course.outcomes.map((outcome, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Interested in a course for your board?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get in touch to discuss the right program for your directors.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CoursesInfoPage;
